/**
 * Shared badge primitives for the rules library — severity, status, and
 * rule-type pills. Kept here so RuleCard and RuleDetail render the same
 * visual language without duplication.
 */
import * as React from 'react';
import { cn } from '../../lib/utils';
import type { RuleSeverity, RuleStatus, RuleType } from '../../types/rules';

const SEVERITY_STYLES: Record<RuleSeverity, string> = {
  must: 'bg-red-50 text-red-700 border-red-200',
  should: 'bg-amber-50 text-amber-800 border-amber-200',
  suggested: 'bg-slate-100 text-slate-700 border-slate-200',
};

const STATUS_STYLES: Record<RuleStatus, string> = {
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  draft: 'bg-slate-100 text-slate-600 border-slate-200',
  proposed: 'bg-blue-50 text-blue-700 border-blue-200',
  retired: 'bg-zinc-100 text-zinc-500 border-zinc-200 line-through',
};

const RULE_TYPE_LABELS: Record<RuleType, string> = {
  trigger: 'Trigger',
  activity_resolution: 'Activity Resolution',
  validation: 'Validation',
  quantity_heuristic: 'Quantity Heuristic',
  companion: 'Companion',
};

interface PillProps extends React.HTMLAttributes<HTMLSpanElement> {
  tone?: string;
}

const Pill: React.FC<PillProps> = ({ className, children, tone, ...rest }) => (
  <span
    className={cn(
      'inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide',
      tone,
      className,
    )}
    {...rest}
  >
    {children}
  </span>
);

export const SeverityBadge: React.FC<{ severity: RuleSeverity }> = ({ severity }) => (
  <Pill tone={SEVERITY_STYLES[severity] || SEVERITY_STYLES.should}>{severity}</Pill>
);

export const StatusPill: React.FC<{ status: RuleStatus }> = ({ status }) => (
  <Pill tone={STATUS_STYLES[status] || STATUS_STYLES.active}>{status}</Pill>
);

export const RuleTypeBadge: React.FC<{ ruleType: RuleType }> = ({ ruleType }) => (
  <Pill
    tone="bg-slate-50 text-slate-600 border-slate-200 normal-case tracking-normal font-normal"
  >
    {RULE_TYPE_LABELS[ruleType] || ruleType}
  </Pill>
);

export const TagChip: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="inline-flex items-center rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-600">
    {children}
  </span>
);

export { RULE_TYPE_LABELS };
